import { Link } from "@inertiajs/react"
import { AppLayout } from "../../components/AppLayout"
import { Badge } from "../../components/Badge"
import { Field } from "../../components/Field"
import { MiniBarChart } from "../../components/MiniBarChart"
import { TextInput } from "../../components/TextInput"
import { getCategoryLabel } from "../../utils/categories"

type CardSummary = {
  id: number
  title: string
  slug: string
  user_name: string
  user_username: string
  is_published: boolean
  categories: string[]
  public_url: string
  edit_url: string
  preview_url: string
  analytics_url: string
  updated_at: string
  analytics: {
    visits: number
    unique_visitors: number
    series: Array<{ label: string; value: number }>
  }
}

type Props = {
  cards: CardSummary[]
  new_card_path: string
  admin: boolean
  query: string
  pagination: {
    current_page: number
    total_pages: number
    total_cards: number
  }
}

function buildPageHref(query: string, page: number) {
  const params = new URLSearchParams()
  if (query) params.set("q", query)
  if (page > 1) params.set("page", String(page))
  const search = params.toString()
  return search ? `/admin?${search}` : "/admin"
}

export default function Dashboard({ cards, new_card_path, admin, query, pagination }: Props) {
  return (
    <AppLayout title="לוח הבקרה">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{admin ? "כל הכרטיסים" : "כרטיסים שלי"}</h1>
          <p className="mt-1 text-slate-600">{admin ? "ניהול מהיר של כל הכרטיסים במערכת" : "ניהול מהיר ופשוט של הכרטיסים שלך"}</p>
        </div>
        <Link href={new_card_path} className="btn-primary bg-brand-600">יצירת כרטיס חדש</Link>
      </div>

      {admin ? (
        <form method="get" action="/admin" className="mb-6 grid gap-3 rounded-3xl border border-slate-200 bg-white p-4 sm:grid-cols-[1fr_auto]">
          <Field label="חיפוש כרטיסים">
            <TextInput name="q" defaultValue={query} placeholder="חיפוש לפי שם, כתובת או משתמש" />
          </Field>
          <button className="btn-primary bg-brand-600 self-end">חיפוש</button>
        </form>
      ) : null}

      {cards.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <h2 className="text-xl font-semibold">אין עדיין כרטיסים</h2>
          <p className="mt-2 text-slate-600">אפשר ליצור כרטיס ראשון תוך כמה שניות.</p>
          <Link href={new_card_path} className="btn-primary mt-5 bg-brand-600">יצירת כרטיס חדש</Link>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {cards.map((card) => (
            <article key={card.id} className="glass-card p-5">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold">{card.title}</h2>
                  <p className="text-sm text-slate-500">/{card.slug}</p>
                  {admin ? <p className="mt-1 text-sm text-slate-400">{card.user_name} / @{card.user_username}</p> : null}
                  {card.categories.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {card.categories.slice(0, 3).map((category) => (
                        <span key={category} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                          {getCategoryLabel(category)}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
                <Badge tone={card.is_published ? "green" : "amber"}>{card.is_published ? "פורסם" : "לא פורסם"}</Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link href={card.edit_url} className="btn-secondary">עריכה</Link>
                <Link href={card.analytics_url} className="btn-secondary">אנליטיקה</Link>
                <Link href={card.preview_url} className="btn-secondary">תצוגה מקדימה</Link>
                <a href={card.public_url} className="btn-secondary" target="_blank" rel="noreferrer">פתח כרטיס ציבורי</a>
              </div>
              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-4 grid grid-cols-2 gap-3">
                  <div className="rounded-2xl bg-white p-3">
                    <div className="text-xs text-slate-500">כניסות</div>
                    <div className="mt-1 text-2xl font-semibold">{card.analytics.visits}</div>
                  </div>
                  <div className="rounded-2xl bg-white p-3">
                    <div className="text-xs text-slate-500">מבקרים ייחודיים</div>
                    <div className="mt-1 text-2xl font-semibold">{card.analytics.unique_visitors}</div>
                  </div>
                </div>
                <MiniBarChart points={card.analytics.series} />
              </div>
            </article>
          ))}
        </div>
      )}

      {pagination.total_pages > 1 ? (
        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-500">
            עמוד {pagination.current_page} מתוך {pagination.total_pages} · {pagination.total_cards} כרטיסים
          </div>
          <div className="flex gap-2">
            <Link
              href={buildPageHref(query, Math.max(pagination.current_page - 1, 1))}
              className="btn-secondary"
              preserveScroll
              aria-disabled={pagination.current_page <= 1}
            >
              הקודם
            </Link>
            <Link
              href={buildPageHref(query, Math.min(pagination.current_page + 1, pagination.total_pages))}
              className="btn-secondary"
              preserveScroll
              aria-disabled={pagination.current_page >= pagination.total_pages}
            >
              הבא
            </Link>
          </div>
        </div>
      ) : null}
    </AppLayout>
  )
}
